import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settings } from '@repletoryreach/sdk-api';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useIsOrgAdmin } from '../components/RoleGate';
import { Button, Group, Progress, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../i18n/I18nProvider';
import { GuideText } from './GuideText';
import type { Guide, GuideLink } from './types';

function Links({ links, onGo, onTour }: { links: GuideLink[]; onGo: (to: string) => void; onTour: (id: string) => void }) {
  const t = useT();
  return (
    <div className="rr-guide__links">
      {links.map((l) => (
        <button key={(l.to ?? l.tour ?? '') + l.labelKey} type="button" className="rr-guide__link"
          onClick={() => (l.tour ? onTour(l.tour) : l.to && onGo(l.to))}>{t(l.labelKey)} →</button>
      ))}
    </div>
  );
}

const TICKS_KEY = 'guide.checklist.done';
type Ticks = Record<string, string[]>;

/**
 * Steps ticked by hand, kept per organisation in settings so the whole team sees them (8 October 2026). A business
 * may have done a step its own way, or not need it at all.
 */
function useTicks(guideId: string) {
  const t = useT();
  const qc = useQueryClient();
  const key = ['settings', TICKS_KEY];
  const q = useQuery({
    queryKey: key,
    queryFn: () => settings.getSetting<Ticks>(TICKS_KEY).then((r) => (r.data && typeof r.data === 'object' ? r.data : {})).catch(() => ({} as Ticks)),
    staleTime: 60_000
  });
  const save = useMutation({
    mutationFn: (next: Ticks) => settings.setSetting<Ticks>(TICKS_KEY, next, { scope: 'org', value_type: 'json' }),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<Ticks>(key);
      qc.setQueryData(key, next);
      return { before };
    },
    onError: (_e, _next, ctx) => {
      qc.setQueryData(key, ctx?.before);
      notifyError(t('guide.checklist.saveError'), t('guide.checklist.title'));
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: key }); }
  });
  const mine = new Set(q.data?.[guideId] ?? []);
  const toggle = (id: string) => {
    const next = new Set(mine);
    if (next.has(id)) next.delete(id); else next.add(id);
    save.mutate({ ...(q.data ?? {}), [guideId]: [...next].sort() });
  };
  return { mine, toggle, loading: q.isPending };
}

/**
 * The setup steps. Each is ticked by itself from what is really set up, or by hand by an owner or admin; a step
 * ticked by hand can be unticked, one found in the set-up cannot.
 */
function Checklist({ guide, onGo }: { guide: Guide; onGo: (to: string) => void }) {
  const t = useT();
  const canTick = useIsOrgAdmin();
  const { items, loading } = guide.useChecklist!();
  const ticks = useTicks(guide.id);
  const isDone = (i: { id: string; done: boolean | undefined }) => i.done === true || ticks.mine.has(i.id);
  const required = items.filter((i) => !i.optional);
  const done = required.filter(isDone).length;
  return (
    <section className="rr-guide__checklist" aria-labelledby="rr-guide-checklist" data-testid="guide-checklist">
      <Group justify="space-between" wrap="nowrap">
        <Text id="rr-guide-checklist" role="bodySmall" fw={700}>{t('guide.checklist.title')}</Text>
        {!loading && <Text role="caption" c="var(--rr-color-text-muted)">{t('guide.checklist.count', { done, total: required.length })}</Text>}
      </Group>
      <Progress value={required.length ? (done / required.length) * 100 : 0} size="sm" aria-label={t('guide.checklist.title')} />
      {canTick && <Text role="caption" c="var(--rr-color-text-muted)">{t('guide.checklist.tickHint')}</Text>}
      <ol className="rr-guide__steps">
        {items.map((item) => {
          const auto = item.done === true;
          const byHand = !auto && ticks.mine.has(item.id);
          const ticked = auto || byHand;
          const title = t(item.titleKey);
          return (
            <li key={item.id} className={ticked ? 'is-done' : ''} data-step={item.id}>
              <button
                type="button"
                role="checkbox"
                aria-checked={ticked}
                className="rr-guide__tick"
                disabled={auto || !canTick || ticks.loading}
                aria-label={ticked ? t('guide.checklist.untick', { step: title }) : t('guide.checklist.tick', { step: title })}
                title={auto ? t('guide.checklist.autoDone') : canTick ? (ticked ? t('guide.checklist.untick', { step: title }) : t('guide.checklist.tick', { step: title })) : undefined}
                onClick={() => ticks.toggle(item.id)}
                data-testid={`tick-${item.id}`}
              >
                {ticked ? '✓' : ''}
              </button>
              <div className="rr-guide__step">
                <Text role="bodySmall" fw={600}>
                  {title}
                  {item.optional && <Text inline role="caption" c="var(--rr-color-text-muted)"> {t('guide.checklist.optional')}</Text>}
                </Text>
                <Text role="caption" c="var(--rr-color-text-muted)">{t(item.hintKey)}</Text>
                {auto && <Text role="caption" c="var(--rr-color-success)">{t('guide.checklist.autoDone')}</Text>}
                {byHand && <Text role="caption" c="var(--rr-color-success)">{t('guide.checklist.manualDone')}</Text>}
                {!ticked && (
                  <button type="button" className="rr-guide__link" onClick={() => onGo(item.to)}>{t('guide.checklist.go')} →</button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** The written guide: what to set up, in order, then each part of the module with notes and links. */
export function GuideDrawer({ guide, opened, onClose, onStartTour }: { guide: Guide; opened: boolean; onClose: () => void; onStartTour: (howTo?: string) => void }) {
  const t = useT();
  const nav = useNavigate();
  const go = (to: string) => { onClose(); nav(to); };
  const jump = (id: string) => document.getElementById(`rr-guide-${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  return (
    <Drawer opened={opened} onClose={onClose} title={t(guide.titleKey)} size="lg" position="right">
      <Stack gap="lg" className="rr-guide" data-testid="guide-drawer">
        <GuideText text={t(guide.introKey)} />
        <Group gap="xs">
          <Button size="xs" onClick={() => { onClose(); onStartTour(); }}>{t('guide.help.tour')}</Button>
        </Group>
        {guide.howTos && guide.howTos.length > 0 && (
          <section className="rr-guide__howtos" aria-labelledby="rr-guide-howtos">
            <Text id="rr-guide-howtos" role="bodySmall" fw={700}>{t('guide.howTos')}</Text>
            {guide.howTos.map((h) => (
              <button key={h.id} type="button" className="rr-guide__howto" onClick={() => { onClose(); onStartTour(h.id); }}>
                <strong>{t(h.titleKey)}</strong><span>{t(h.hintKey)}</span>
              </button>
            ))}
          </section>
        )}
        {guide.useChecklist && <Checklist guide={guide} onGo={go} />}
        <nav aria-label={t('guide.contents')} className="rr-guide__toc">
          <Text role="bodySmall" fw={700}>{t('guide.contents')}</Text>
          <ol>
            {guide.sections.map((s) => (
              <li key={s.id}><button type="button" className="rr-guide__link" onClick={() => jump(s.id)}>{t(s.titleKey)}</button></li>
            ))}
          </ol>
        </nav>
        {guide.sections.map((s) => (
          <section key={s.id} id={`rr-guide-${s.id}`} className="rr-guide__section" aria-labelledby={`rr-guide-h-${s.id}`}>
            <h3 id={`rr-guide-h-${s.id}`} className="rr-guide__heading">{t(s.titleKey)}</h3>
            <Stack gap="xs">
              {s.bodyKeys.map((k) => <GuideText key={k} text={t(k)} />)}
              {s.noteKeys?.map((k) => (
                <Alert key={k} color="info" title={t('guide.note')}><GuideText text={t(k)} /></Alert>
              ))}
              {s.links && s.links.length > 0 && <Links links={s.links} onGo={go} onTour={(id) => { onClose(); onStartTour(id); }} />}
            </Stack>
          </section>
        ))}
      </Stack>
    </Drawer>
  );
}
